import { Button } from "@mui/material";
import React from "react";
import AccordionList from "src/components/Form/Elements/AccordionList/AccordionList";
import AccordionListNoAPICalls from "src/components/Form/Elements/AccordionList/AccordionListNoAPICalls";
import DateInput from "src/components/Form/Elements/Date";
import FileUpload from "src/components/Form/Elements/FileUpload";
import ParsedFileUpload from "src/components/Form/Elements/ParsedFileUpload";
import Select from "src/components/Form/Elements/Select";
import TextField, { TextFieldEditorHandle } from "src/components/Form/Elements/TextField";
import { TitleWithContext } from "src/components/Form/TitleWithContext/TitleWithContext";
import { StepInputControls } from "src/components/FormStep/StepInputControls";
import { useSnackbar } from "..";
import {
  DocumentVersion,
  RoadmapTasks,
} from "../../stores/models";
import { StepValue, TemplateConfig, TemplateQuestion } from "../../types";
import { ContextState, handleAnswerItemsCSVUpload } from "./utils";

type Props = {
  stepValue: StepValue;
  disabled?: boolean;
  onChange?: (step: TemplateQuestion, value: StepValue) => void;
  onRegenerateSuggestion?: () => void;
  onReRunTransformer?: () => void;
  step: TemplateQuestion;
  onUndo?: () => void;
  onRedo?: () => void;
  documentVersion?: DocumentVersion;
  title?: string;
  textFieldReadOnly?: boolean;
  contextConfig: ContextState;
  deviceId: string;
  templatesConfig: TemplateConfig;
  tasks: RoadmapTasks;
  onAutoSuggest?: () => void;
  textFieldEditorRef?: React.Ref<TextFieldEditorHandle>;
};

export const ElementMapper: React.FC<Props> = ({
  disabled,
  onChange,
  onRegenerateSuggestion,
  onReRunTransformer,
  onUndo,
  onRedo,
  step,
  stepValue,
  documentVersion,
  title = "Answer",
  textFieldReadOnly = false,
  contextConfig,
  deviceId,
  templatesConfig,
  tasks,
  onAutoSuggest,
  textFieldEditorRef,
}) => {
  const { showSnackbar, hideSnackbar } = useSnackbar();
  const { element } = step;

  const hasTransformer = !!element.transformerConfig;
  const hasActions =
    !!onChange ||
    !!onRegenerateSuggestion ||
    !!onReRunTransformer ||
    !!onUndo ||
    !!onRedo;

  const renderElement = () => {
    switch (element.type) {
      case "fileUpload":
        return (
          <FileUpload
            element={element}
            answerFileId={stepValue.answerFileId}
            onChange={(_, value) => onChange?.(step, value)}
          />
        );

      case "parsedFileUploadElement":
        return (
          <ParsedFileUpload
            element={element}
            value={stepValue.answer || ""}
            onChange={(_, value) => onChange?.(step, value)}
          />
        );

      case "table":
        return (
          <TextField
            ref={textFieldEditorRef}
            disabled={disabled}
            onChange={(_, value) => onChange?.(step, value)}
            element={element}
            value={stepValue.answer || ""}
            textFieldReadOnly={textFieldReadOnly}
          />
        );

      case "textField":
        return (
          <TextField
            ref={textFieldEditorRef}
            disabled={disabled}
            onChange={(_, value) => onChange?.(step, value)}
            element={element}
            value={stepValue.answer || ""}
            textFieldReadOnly={textFieldReadOnly}
          />
        );

      case "select":
        return (
          <Select
            disabled={disabled}
            onChange={(_, value) => onChange?.(step, value)}
            value={stepValue.answer || ""}
            element={element}
          />
        );

      case "answerItemsElement":
        return documentVersion !== undefined ? (
          <AccordionList
            disabled={disabled}
            onChange={(value) => onChange?.(step, value)}
            answerItems={stepValue.answerItems || []}
            step={step}
            element={element}
            documentVersion={documentVersion}
          />
        ) : (
          <AccordionListNoAPICalls
            disabled={disabled}
            onChange={(value) => onChange?.(step, value)}
            answerItems={stepValue.answerItems || []}
            element={element}
          />
        );

      case "dateInput":
        return (
          <DateInput
            style={element.style}
            hideLabel={element.options.hideLabel}
            disabled={disabled}
            onChange={(value) => onChange?.(step, value)}
            value={stepValue.answer || ""}
          />
        );

      default:
        return null;
    }
  };

  const importAnswerItems = async (file: File) => {
    try {
      hideSnackbar();
      if (element.type !== "answerItemsElement") {
        throw new Error("Please upload a CSV for answer items.");
      }
      const existingItems = stepValue.answerItems || [];
      const importedItems = await handleAnswerItemsCSVUpload(
        file,
        element,
        existingItems
      );
      onChange?.(step, { answerItems: [...existingItems, ...importedItems] });
    } catch (error: any) {
      showSnackbar(error.message || "Please upload a valid CSV file.");
    }
  };

  return (
    <div className="flex flex-col">
      {/* Min height is to prevent the text from shifting when the controls are hidden */}
      <div className="flex items-center justify-between gap-x-3 min-h-[34px]">
        <div className="flex-1 min-w-0">
          <TitleWithContext
            contextConfig={contextConfig}
            title={title || "Answer"}
            deviceId={deviceId}
            templatesConfig={templatesConfig}
            tasks={tasks}
          />
        </div>

        <div className="flex items-center justify-end gap-x-3 flex-shrink-0 pb-1">
          {onAutoSuggest && (
            <Button variant="text" onClick={onAutoSuggest}>
              <span
                style={{
                  background:
                    "linear-gradient(90deg, #1976d2, #A07CFE, #FE8FB5, #FFBE7B, #1976d2)",
                  backgroundSize: "200% 100%",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  animation: "shine-right 5s infinite linear",
                  fontSize: "0.8125rem",
                  lineHeight: "1.75",
                }}
              >
                Get Suggestion
              </span>
            </Button>
          )}
          {hasActions ? (
            <StepInputControls
              hasCSVUpload={element.type === "answerItemsElement"}
              hasTransformer={hasTransformer}
              onRegenerateSuggestion={onRegenerateSuggestion}
              onReRunTransformer={onReRunTransformer}
              onCSVUpload={(file: File) => {
                if (element.type === "answerItemsElement") {
                  importAnswerItems(file);
                }
              }}
              onUndo={undefined} // Intentionally disabled for now
              onRedo={undefined} // Intentionally disabled for now
            />
          ) : null}
        </div>
      </div>
      {renderElement()}
    </div>
  );
};
